/**
 * URLResolver - Single Source of Truth for all API URLs
 *
 * RULES:
 * 1. ALWAYS use this module to get API URLs
 * 2. NEVER define API_BASE_URL in other files
 * 3. NEVER use import.meta.env.VITE_SERVERLESS_API_URL directly
 *
 * This module consolidates 8+ different implementations of API_BASE_URL
 * with inconsistent fallbacks into a single, consistent source.
 */

import { isLocalMode, isServerlessMode, isElectronMode } from '../utils/apiMode';

/**
 * Get the local API URL (Flask local server)
 *
 * Priority:
 * 1. VITE_LOCAL_API_URL environment variable
 * 2. Default: http://localhost:5001 (cross-platform consistent)
 *
 * Note: The backend uses port 5001 on all platforms for consistency.
 * See constants/api/server_config.py SERVER_PORT_DEFAULT.
 */
export function getLocalApiUrl(): string {
  // Priority 1: Explicit environment variable
  if (import.meta.env.VITE_LOCAL_API_URL) {
    return import.meta.env.VITE_LOCAL_API_URL;
  }

  // Priority 2: Default port 5001 (consistent across all platforms)
  // Backend SERVER_PORT_DEFAULT is 5001 for cross-platform consistency
  return 'http://localhost:5001';
}

/**
 * Get the serverless API URL (Cloud Run)
 *
 * Returns empty string if not configured.
 */
export function getServerlessApiUrl(): string {
  return import.meta.env.VITE_SERVERLESS_API_URL || '';
}

/**
 * Get the API base URL based on current mode
 *
 * This is the main function - use this in most cases.
 *
 * Priority:
 * 1. Electron mode -> Local API
 * 2. Serverless mode with configured URL -> Serverless API
 * 3. Local mode -> Local API
 * 4. Fallback: Serverless if available, else Local
 */
export function getApiBaseUrl(): string {
  // Electron desktop mode always uses local Flask API
  if (isElectronMode()) {
    return getLocalApiUrl();
  }

  // Serverless mode (production or staging)
  if (isServerlessMode()) {
    const serverlessUrl = getServerlessApiUrl();
    if (serverlessUrl) {
      return serverlessUrl;
    }
  }

  // Local development mode
  if (isLocalMode()) {
    return getLocalApiUrl();
  }

  // Fallback: Try serverless first, then local
  const serverlessUrl = getServerlessApiUrl();
  if (serverlessUrl) {
    return serverlessUrl;
  }

  return getLocalApiUrl();
}

/**
 * Build a full API URL from a relative path
 *
 * @param endpoint - Relative path (e.g., "/api/v1/generate" or "api/v1/generate")
 * @returns Full URL (e.g., "https://api.example.com/api/v1/generate")
 *
 * @example
 * buildApiUrl('/api/v1/generate') // "https://api.example.com/api/v1/generate"
 * buildApiUrl('api/v1/generate')  // "https://api.example.com/api/v1/generate"
 */
export function buildApiUrl(endpoint: string): string {
  const baseUrl = getApiBaseUrl();
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
}

/**
 * Get the dedicated Cloud API URL for cloud-only features.
 *
 * In Electron mode, VITE_SERVERLESS_API_URL points to the local Flask backend,
 * but cloud-only features (jackpot, billing, payments) must reach Cloud Run.
 * VITE_CLOUD_API_URL provides this dedicated URL.
 */
export function getCloudApiUrl(): string {
  return import.meta.env.VITE_CLOUD_API_URL || '';
}

/**
 * Build a full API URL that always targets the Cloud Run serverless API.
 *
 * Use this for cloud-only features (jackpot, billing, payments) that must
 * reach the serverless backend even when running in Electron desktop mode.
 *
 * Priority:
 * 1. VITE_CLOUD_API_URL (dedicated cloud URL, always points to Cloud Run)
 * 2. VITE_SERVERLESS_API_URL (works correctly in web mode, but in Electron
 *    this points to localhost which lacks cloud-only endpoints)
 * 3. Normal API base URL fallback
 */
export function buildCloudApiUrl(endpoint: string): string {
  const cloudUrl = getCloudApiUrl();
  const serverlessUrl = getServerlessApiUrl();
  const baseUrl = cloudUrl || serverlessUrl || getApiBaseUrl();
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
}

/**
 * Get WebSocket URL from API base URL
 *
 * Converts http(s) to ws(s) protocol.
 *
 * @param path - WebSocket path (e.g., "/ws/health")
 * @returns Full WebSocket URL
 */
export function getWebSocketUrl(path: string): string {
  const baseUrl = getApiBaseUrl();
  const wsUrl = baseUrl.replace(/^http/, 'ws');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${wsUrl}${normalizedPath}`;
}
